/**
 * useShellLiveMap - the map's live answers, folded into what the panels see.
 *
 * Two hooks, called in this order by the shell:
 *
 * - `useShellLiveMap`: the terrain's solids and the live modifiers between
 *   THIS client's actor and the ACTIVE target (cover, range band, facing,
 *   arc). Only a vehicle has arcs, and only the active target is aimed at.
 * - `useShellLiveTabs`: what the ground does to everyone (one computation,
 *   two consumers - the projection and the map's marker notes), and the
 *   roster's tabs with both folded in. COVER, FACING and the ARRIVAL ARC are
 *   relations between two combatants, so they reach only the tab being aimed
 *   at; SURFACES reach everybody, because everyone stands somewhere. All of
 *   it is derived and none of it is stored - `projectDefenderFrom` stamps it.
 *
 * Extracted from DeathWatchSimulator.
 */
import { useMemo, useRef } from 'react';
import useMapSolids from './useMapSolids';
import { projectDefenderFrom } from '../engine/roster/projection';
import { resolveBodyShape } from '../engine/bodyShape';
import useLiveMapModifiers from './useLiveMapModifiers';
import useSurfaceEffects from './useSurfaceEffects';

/**
 * The log line for a terrain change that moved people: who now stands on the
 * new terrain, and who was pulled back onto the map. Null when nobody moved.
 */
export function terrainLiftText(roster, { lifted, movedInside }) {
  const name = (id) => roster.find((c) => c.id === id)?.name || id;
  const parts = [];
  if (lifted.length) parts.push(`${lifted.map(name).join(', ')} now stand on the new terrain`);
  if (movedInside.length) parts.push(`${movedInside.map(name).join(', ')} were pulled back onto the map`);
  return parts.length ? `🗺 ${parts.join('; ')}.` : null;
}

/** The terrain's voxel size in metres, or 1 with no voxels. */
export function cellSizeOf(terrain) {
  return terrain?.voxels?.size > 0 ? terrain.voxels.size : 1;
}

/** One tab with the live map folded in - the same tab when nothing applies. */
function foldTab(t, combatant, live) {
  const aimed = t.id === live.activeTab;
  const cover = aimed ? live.cover : null;
  const arc = aimed ? live.arc : null;
  const facing = aimed ? live.vehicleFacing : null;
  const surface = live.surfaceEffects[t.id] ?? null;
  if (!cover && !surface && !facing && !arc) return t;
  const target = projectDefenderFrom(combatant, cover, surface, live.coverConflictMode, facing);
  return { ...t, target: arc ? { ...target, arrivalArc: arc } : target };
}

/** The roster's tabs with the live map folded in; the same array when nothing is live. */
export function foldLiveMap(rosterTabs, roster, live) {
  const hasSurfaces = Object.keys(live.surfaceEffects).length > 0;
  if (!live.cover && !live.vehicleFacing && !live.arc && !hasSurfaces) return rosterTabs;
  return rosterTabs.map((t) => {
    const combatant = roster.find((c) => c.id === t.id);
    return combatant ? foldTab(t, combatant, live) : t;
  });
}

/**
 * @param {object} args - terrain, rosterTabs, actorId, activeTab, tokens,
 *   weaponRange, isGm, enabled (the derived-values rules), arcPreset, relationRules (the table's rulings), roster
 *   and hiddenIds (the Objects that count as terrain, and the GM's hidden ones).
 * @returns {{ mapSolids: object[], liveMap: object }}
 */
export function useShellLiveMap({
  terrain,
  rosterTabs,
  actorId,
  activeTab,
  tokens,
  weaponRange,
  isGm,
  enabled,
  arcPreset,
  relationRules,
  roster,
  hiddenIds,
}) {
  // The terrain's solids and every Object that counts as terrain (2026-10-26) - see the hook.
  const { solids: mapSolids } = useMapSolids(terrain, roster, tokens, hiddenIds);
  const targetIsVehicle = resolveBodyShape(rosterTabs.find((t) => t.id === activeTab)?.target) === 'vehicle';
  const liveMap = useLiveMapModifiers({
    actorId,
    targetId: activeTab,
    tokens,
    solids: mapSolids,
    weaponRange,
    isGm,
    enabled,
    targetIsVehicle,
    arcPreset,
    relationRules,
  });
  return { mapSolids, liveMap };
}

/**
 * @param {object} args - roster, rosterTabs, tokens, terrain, activeTab,
 *   liveMap, coverConflictMode, surfacesOn (the house rule).
 * @returns {{ surfaceEffects: object, surfaceEffectsRef: {current: object}, tabs: object[] }}
 *   `surfaceEffectsRef` is for the shell's stable writers, which must not be
 *   rebuilt every time somebody steps on or off a surface.
 */
export function useShellLiveTabs({
  roster,
  rosterTabs,
  tokens,
  terrain,
  activeTab,
  liveMap,
  coverConflictMode,
  surfacesOn,
}) {
  const surfaceEffects = useSurfaceEffects({
    roster,
    tokens,
    zones: terrain?.zones,
    cellSize: cellSizeOf(terrain),
    enabled: surfacesOn,
  });
  const surfaceEffectsRef = useRef({});
  surfaceEffectsRef.current = surfaceEffects;
  const { cover, vehicleFacing, arc } = liveMap;
  const tabs = useMemo(
    () => foldLiveMap(rosterTabs, roster, { activeTab, cover, vehicleFacing, arc, surfaceEffects, coverConflictMode }),
    [rosterTabs, cover, vehicleFacing, arc, roster, activeTab, surfaceEffects, coverConflictMode],
  );
  return { surfaceEffects, surfaceEffectsRef, tabs };
}
